import type { Prop } from "../models/Prop";
import { getEnvironmentPropPositionId } from "../models/Prop";
import type Take from "../models/Take";
import type Take3D from "../models/Take3D";

export interface PropReference {
  propId: string;
  location: string;
}

export interface PropReferenceProject {
  props: Prop[];
  takes3D: Take3D[];
  takes360?: Take[];
}

/** Deleting a situation also deletes its linked descendants. */
export function getDeletedPropIds(props: Prop[], id: string): Set<string> {
  const ids = new Set([id]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const prop of props) {
      if (prop.link && ids.has(prop.link.parentId) && !ids.has(prop.id)) {
        ids.add(prop.id);
        changed = true;
      }
    }
  }
  return ids;
}

// Only identifier fields are references; text, tags, names and property values
// may coincidentally contain the same string and must not block deletion.
const interactionReferenceFields = new Set([
  "propGuid", "propId", "npcId", "lookAt", "targetId", "posId", "positionId", "cameraId", "takeLocationId",
]);

export function getPropReferences(project: PropReferenceProject, id: string): PropReference[] {
  const ids = getDeletedPropIds(project.props, id);
  const references: PropReference[] = [];
  const seen = new Set<string>();
  const add = (value: unknown, location: string) => {
    if (typeof value !== "string" || !value) return;
    const referencedId = ids.has(value) ? value : project.props.find((prop) =>
      ids.has(prop.id) && (value.startsWith(`${prop.id}/`) ||
        (prop.environmentMetadataId && value === getEnvironmentPropPositionId(prop.environmentMetadataId)))
    )?.id;
    if (!referencedId) return;
    const key = JSON.stringify([referencedId, location]);
    if (seen.has(key)) return;
    seen.add(key);
    const prop = project.props.find((candidate) => candidate.id === referencedId);
    references.push({ propId: value, location: `${location} — ${prop?.name || value}` });
  };

  const visit = (value: unknown, location: string, ancestors = new Set<object>()) => {
    if (!value || typeof value !== "object" || ancestors.has(value)) return;
    const nextAncestors = new Set(ancestors).add(value);
    if (Array.isArray(value)) {
      value.forEach((item, index) => visit(item, `${location} / ${index + 1}`, nextAncestors));
      return;
    }
    const record = value as Record<string, unknown>;
    const label = typeof record.type === "string" && typeof record.name === "string"
      ? `${location} / ${record.name}` : location;
    for (const [key, child] of Object.entries(record)) {
      if (interactionReferenceFields.has(key) || (key === "source" && record.type === "dynamicInteraction")) {
        add(child, label);
      }
      // Edges reference node IDs, not Props. Properties contain free-form values.
      if (key !== "edges" && key !== "properties") visit(child, label, nextAncestors);
    }
  };

  for (const take of [...project.takes3D, ...(project.takes360 ?? [])]) {
    const location = `Take: ${take.name || take.id}`;
    visit(take.interactions, `${location} / Interactions`);
    const take3D = take as Take3D;
    visit(take3D.sequence, `${location} / Sequence`);
    visit(take3D.mainCamera, `${location} / Camera`);
    add(take3D.startPropInspect?.propId, `${location} / Starting view`);
    for (const placed of take3D.props ?? []) {
      const preset = project.props.find((prop) => prop.id === placed.instanceId);
      const automaticEnvironmentPlacement = preset?.environmentMetadataId &&
        placed.positionId === getEnvironmentPropPositionId(preset.environmentMetadataId);
      // This placement is removed atomically by deletePropPreset itself.
      if (!automaticEnvironmentPlacement) add(placed.instanceId, `${location} / Placement`);
      if (!automaticEnvironmentPlacement) add(placed.positionId, `${location} / Prop position`);
    }
    for (const actor of take3D.actors ?? []) {
      add(actor.prop, `${location} / Actor ${actor.actorId} / Held Prop`);
      add(actor.positionId, `${location} / Actor ${actor.actorId} / Position`);
      add(actor.lookat, `${location} / Actor ${actor.actorId} / Look At`);
    }
    Object.keys(take3D.attachments ?? {}).forEach((propId) => add(propId, `${location} / Attachment`));
    Object.values(take3D.situations ?? {}).forEach((propId) => add(propId, `${location} / Situation`));
  }
  return references;
}
